import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from '../../components/ProductCard/ProductCard';
import SearchBar from '../../components/SearchBar/SearchBar';
import { Product } from '../../types';
import { productsAPI, categoriesAPI } from '../../services/api';
import './ProductsPage.css';
import ServiceBadges from '../../components/ServiceBadges/ServiceBadges';
import AdvancedFilters, { FilterOptions } from '../../components/AdvancedFilters/AdvancedFilters';

interface Category {
    id: number;
    slug: string;
    name: string;
    icon?: string;
}

const ProductsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                // Загружаем продукты
                const productsResponse = await productsAPI.getAll({
                    language: i18n.language
                });

                const productsData = productsResponse.data.data.map((p: any) => ({
                    id: p.id.toString(),
                    name: { [i18n.language]: p.name },
                    description: { [i18n.language]: p.description },
                    category: p.category,
                    cuisine: p.cuisine,
                    images: Array.isArray(p.images) ? p.images : [],
                    featured: p.display_order <= 3,
                    price: p.price
                }));

                setProducts(productsData);
                setFilteredProducts(productsData);

                // Загружаем категории из БД
                const categoriesResponse = await categoriesAPI.getAll(i18n.language);
                setCategories(categoriesResponse.data);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [i18n.language]);

    const applyFilters = (filters: FilterOptions) => {
        let filtered = [...products];
        const currentLang = i18n.language as 'en' | 'ru' | 'kk';

        // Search filter
        if (searchQuery.trim()) {
            filtered = filtered.filter(p =>
                p.name[currentLang].toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.description[currentLang].toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.category.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        // Category filter
        if (filters.categories.length > 0) {
            filtered = filtered.filter(p => filters.categories.includes(p.category));
        }

        // Dietary filter
        if (filters.dietary.length > 0) {
            filtered = filtered.filter(p =>
                p.dietaryInfo?.some(diet => filters.dietary.includes(diet))
            );
        }

        // Allergen filter (exclude products with these allergens)
        if (filters.allergens.length > 0) {
            filtered = filtered.filter(p =>
                !p.allergens?.some(allergen => filters.allergens.includes(allergen))
            );
        }

        // Sorting
        switch (filters.sortBy) {
            case 'rating':
                filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
                break;
            case 'name':
                filtered.sort((a, b) => a.name[currentLang].localeCompare(b.name[currentLang]));
                break;
            case 'calories':
                filtered.sort((a, b) => (a.nutrition?.calories || 0) - (b.nutrition?.calories || 0));
                break;
            case 'featured':
            default:
                filtered.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
        }

        setFilteredProducts(filtered);
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);
        // Trigger filter re-application
        setTimeout(() => {
            applyFilters({ dietary: [], allergens: [], categories: [], sortBy: 'featured' });
        }, 0);
    };

    const handleProductClick = (product: Product) => {
        if (window.ym) {
            window.ym(12345678, 'reachGoal', 'product_view');
        }
        navigate(`/products/${product.id}`);
    };

    return (
        <div className="products-page">
            <motion.div
                className="page-header"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <h1>{t('products.title')}</h1>
                <p>{t('products.subtitle')}</p>
            </motion.div>

            <div className="products-container">
                <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder={t('products.search') || "Search products..."}
                />

                <AdvancedFilters
                    onFilterChange={applyFilters}
                    categories={categories}
                />

                <ServiceBadges />

                {loading ? (
                    <div className="loading-spinner">Loading products...</div>
                ) : (
                    <>
                        {filteredProducts.length > 0 && (
                            <div className="results-info">
                                <p>{t('products.foundCount', { count: filteredProducts.length })}</p>
                            </div>
                        )}

                        <motion.div
                            className="products-grid"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                        >
                            {filteredProducts.map((product, index) => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                >
                                    <ProductCard
                                        product={product}
                                        onClick={() => handleProductClick(product)}
                                    />
                                </motion.div>
                            ))}
                        </motion.div>
                    </>
                )}

                {!loading && filteredProducts.length === 0 && (
                    <div className="no-products">
                        <p>{t('products.noProductsTitle')}</p>
                        <p>{t('products.noProductsHint')}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductsPage;
